import { DatadogProvider } from '@datadog/openfeature-browser';
import {
    type Client,
    type EvaluationContext,
    OpenFeature,
    ProviderEvents,
} from '@openfeature/react-sdk';

import * as configurationManager from '../config/configurationManager';
import * as stringUtil from '../string/stringUtil';

/**
 * Datadog OpenFeature client for Back Office.
 *
 * Mirrors the POS pattern (`ignis.pos/src/utils/ddFeatureFlags/ddFeatureFlags.ts`).
 *
 * The provider is initialised lazily on first {@link initializeDDFeatureFlags} call.
 * If Datadog credentials are missing from config, the service stays disabled and
 * every evaluation returns the caller's `defaultValue`.
 */

type InitializeDDFeatureFlagsOptions = {
    enableExposureLogging?: boolean;
    enableFlagEvaluationTracking?: boolean;
    enableRumFeatureFlagTracking?: boolean;
};

/**
 * Dedicated OpenFeature domain for the Datadog provider.
 *
 * Back Office also runs a Split-backed OpenFeature provider
 * (`utils/featureFlags/openFeature.ts`) on the DEFAULT client. If the Datadog
 * provider were bound to the default client too, whichever initialised last
 * would win — and Split was winning, so Datadog flag reads returned the Split
 * "control" value (`FLAG_NOT_FOUND`). Binding Datadog to its own domain keeps
 * the two providers isolated. The domain inherits the global evaluation
 * context set via `OpenFeature.setContext`.
 */
const DD_DOMAIN = 'datadog';

const client: Client = OpenFeature.getClient(DD_DOMAIN);

let isDisabled = false;
let isReady = false;
let isInitialized = false;
let currentOptions: Required<InitializeDDFeatureFlagsOptions> | undefined;
let latestInitializationToken = 0;

const defaultOptions: Required<InitializeDDFeatureFlagsOptions> = {
    enableExposureLogging: true,
    enableFlagEvaluationTracking: true,
    enableRumFeatureFlagTracking: true,
};

function normalizeOptions(
    options: InitializeDDFeatureFlagsOptions = {}
): Required<InitializeDDFeatureFlagsOptions> {
    return { ...defaultOptions, ...options };
}

function hasSameOptions(options: Required<InitializeDDFeatureFlagsOptions>): boolean {
    return (
        currentOptions?.enableExposureLogging === options.enableExposureLogging &&
        currentOptions?.enableFlagEvaluationTracking === options.enableFlagEvaluationTracking &&
        currentOptions?.enableRumFeatureFlagTracking === options.enableRumFeatureFlagTracking
    );
}

function getDatadogCredentials(): { applicationId: string; clientToken: string } | null {
    const config = configurationManager.getConfig();
    const applicationId = config.dataDogRumApplicationIdForBackOffice as string;
    const clientToken = config.dataDogRumClientTokenForBackOffice as string;

    if (stringUtil.isStringNullOrEmpty(applicationId) || stringUtil.isStringNullOrEmpty(clientToken)) {
        return null;
    }

    return { applicationId, clientToken };
}

function getDatadogEnvironment(): string {
    let env = (configurationManager.getConfig().environment as string) || 'local';
    if (stringUtil.areStringSame(env, 'ukwest')) env = 'production';
    return env;
}

function getDatadogService(): string {
    return 'RMS.APP.BACKOFFICE';
}

function getDatadogVersion(): string {
    return (configurationManager.getConfig().version as string) || '0.0.0';
}

/**
 * Initialises (or reconfigures) the Datadog OpenFeature provider.
 * Safe to call multiple times — subsequent calls with the same options are no-ops.
 */
export function initializeDDFeatureFlags(options: InitializeDDFeatureFlagsOptions = {}): void {
    const credentials = getDatadogCredentials();
    if (credentials === null) {
        latestInitializationToken += 1;
        isReady = false;
        isDisabled = true;
        currentOptions = undefined;
        if (process.env.NODE_ENV === 'development') {
            console.warn(
                '[DDFeatureFlags] Datadog feature flags disabled — application id / client token missing from Back Office config.'
            );
        }
        return;
    }

    isDisabled = false;
    const normalizedOptions = normalizeOptions(options);

    if (isInitialized && hasSameOptions(normalizedOptions)) {
        return;
    }

    currentOptions = normalizedOptions;
    isInitialized = true;
    isReady = false;
    const initializationToken = ++latestInitializationToken;

    const provider = new DatadogProvider({
        applicationId: credentials.applicationId,
        clientToken: credentials.clientToken,
        enableExposureLogging: normalizedOptions.enableExposureLogging,
        enableFlagEvaluationTracking: normalizedOptions.enableFlagEvaluationTracking,
        enableRumFeatureFlagTracking: normalizedOptions.enableRumFeatureFlagTracking,
        env: getDatadogEnvironment(),
        service: getDatadogService(),
        site: 'us3.datadoghq.com',
        version: getDatadogVersion(),
    });

    void OpenFeature.setProviderAndWait(DD_DOMAIN, provider)
        .then(() => {
            if (initializationToken !== latestInitializationToken) return;
            isReady = true;
        })
        .catch((error: unknown) => {
            if (initializationToken !== latestInitializationToken) return;
            isReady = false;
            isInitialized = false;
            console.error('[DDFeatureFlags] Failed to initialise Datadog OpenFeature provider.', error);
        });
}

/**
 * Updates the OpenFeature evaluation context for Datadog targeting rules.
 * Call when the active org / franchisor / property changes.
 */
export function setDDFeatureFlagContext(context: {
    orgId?: string | null;
    franchisorId?: string | null;
    propertyId?: string | null;
}): void {
    if (isDisabled) return;

    const targetingKey = context.orgId || context.franchisorId || 'anonymous';
    const evaluationContext: EvaluationContext = {
        targetingKey,
        orgId: context.orgId ?? '',
        franchisorId: context.franchisorId ?? '',
        propertyId: context.propertyId ?? '',
    };

    void OpenFeature.setContext(evaluationContext).catch((error: unknown) => {
        console.error('[DDFeatureFlags] Failed to update Datadog OpenFeature context.', error);
    });
}

/**
 * Synchronous boolean flag read. Returns `defaultValue` when provider is
 * disabled or not yet ready.
 */
export function getBooleanFlag(key: string, defaultValue: boolean): boolean {
    if (isDisabled) return defaultValue;
    if (!isReady) return defaultValue;
    return client.getBooleanValue(key, defaultValue);
}

/**
 * Subscribes a callback to provider lifecycle events.
 * Returns an unsubscribe function.
 */
export function subscribeToFlagChanges(callback: () => void): () => void {
    const handler = (): void => { callback(); };

    client.addHandler(ProviderEvents.Ready, handler);
    client.addHandler(ProviderEvents.ConfigurationChanged, handler);
    client.addHandler(ProviderEvents.ContextChanged, handler);

    return () => {
        client.removeHandler(ProviderEvents.Ready, handler);
        client.removeHandler(ProviderEvents.ConfigurationChanged, handler);
        client.removeHandler(ProviderEvents.ContextChanged, handler);
    };
}

export function __resetDDFeatureFlagsForTesting(): void {
    isDisabled = false;
    isReady = false;
    isInitialized = false;
    currentOptions = undefined;
    latestInitializationToken = 0;
}

export function __isReadyForTesting(): boolean {
    return !isDisabled && isReady;
}
